import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isSupabaseConfigured } from "@/services/supabase-client.service";
import {
    getUnreadCount,
    getUnreadReportsCount,
    subscribeToReferrals,
} from "@/services/referral.service";

export const referralKeys = {
    all: ["referrals"] as const,
    notifications: (practiceId?: string) => [...referralKeys.all, "notifications", practiceId].filter(Boolean),
};

export function useReferralNotificationCountsQuery(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const unsubscribe = subscribeToReferrals(practiceId, () => {
            void queryClient.invalidateQueries({
                queryKey: referralKeys.notifications(practiceId),
            });
        });

        return unsubscribe;
    }, [practiceId, queryClient]);

    return useQuery({
        queryKey: referralKeys.notifications(practiceId),
        queryFn: async () => {
            const [unreadReferrals, unreadReports] = await Promise.all([
                getUnreadCount(practiceId!),
                getUnreadReportsCount(practiceId!),
            ]);

            return {
                unreadReferrals,
                unreadReports,
                total: unreadReferrals + unreadReports,
            };
        },
        enabled: !!practiceId,
        staleTime: 30_000,
    });
}
